import { useMemo, useState, useEffect, useRef, Fragment } from "react";
import { Autocomplete, TextField, AutocompleteProps } from "@mui/material";
import { Star, StarOff } from "lucide-react";
import { QueryContact, QueryProspect, QueryTypes } from "@/api/queries/types";
import { useGetSavedQuery2 } from "@/api/queries/useGetSavedQuery";

interface ContactWithSeparator extends QueryContact {
  _showSeparator?: boolean;
  _isProspect?: boolean;
}

interface ContactAutocompleteProps
  extends Omit<
    AutocompleteProps<QueryContact, false, false, false>,
    "options" | "renderInput" | "inputRef"
  > {
  label?: string;
  size?: "small" | "medium";
  placeholder?: string;
  contactId?: string;
  excludeContactIds?: string[];
  includeContactIds?: string[];
  disableFavorites?: boolean;
  includeProspects?: boolean;
}

const FAVORITES_KEY = "contactFavorites";

export const ContactAutocomplete = ({
  label = "Select contact",
  size = "small",
  placeholder,
  disabled,
  contactId,
  excludeContactIds,
  includeContactIds,
  disableFavorites,
  includeProspects,
  onChange: onChangeProp,
  ...props
}: ContactAutocompleteProps) => {
  const { data: contactsData, loading } = useGetSavedQuery2<QueryContact>(
    "advPortalContactsSimple",
    {
      contactName: "%",
    }
  );

  const { data: prospects, loading: loadingProspects } =
    useGetSavedQuery2<QueryProspect>(
      "advPortalProspects",
      {
        contactName: "%",
      },
      !includeProspects,
      "cache-and-network",
      true
    );

  const [favorites, setFavorites] = useState<string[]>(() => {
    const saved = localStorage.getItem(FAVORITES_KEY);
    return saved ? JSON.parse(saved) : [];
  });

  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<QueryContact | null>(null);

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  // Sync selectedValue when contactId or contactsData changes
  useEffect(() => {
    if (contactId && contactsData) {
      const found = contactsData.find((c) => c.contactId === contactId) || null;
      setSelectedValue(found);
    }
  }, [contactId, contactsData]);

  const sortedContacts = useMemo(() => {
    const prospectContacts: QueryContact[] = includeProspects
      ? (prospects || []).map(
          (p) =>
            ({
              ...({} as QueryContact),
              contactId: p.contactId,
              name: p.name,
              _isProspect: true,
            } as QueryContact & { _isProspect: boolean })
        )
      : [];

    const allData = [...(contactsData || []), ...prospectContacts].sort(
      (a, b) => a.name.localeCompare(b.name, "no", { sensitivity: "base" })
    );

    const favoriteContacts = disableFavorites
      ? []
      : allData.filter((contact) => favorites.includes(contact.contactId));
    const nonFavoriteContacts = allData.filter(
      (contact) => !favorites.includes(contact.contactId)
    );

    // Mark the first non-favorite item with a separator
    const mappedNonFavorites = nonFavoriteContacts.map((contact, index) => ({
      ...contact,
      _showSeparator: index === 0,
    }));

    const allContacts = [...favoriteContacts, ...mappedNonFavorites].filter(
      (contact) =>
        includeContactIds
          ? includeContactIds.includes(contact.contactId)
          : !excludeContactIds || !excludeContactIds.includes(contact.contactId)
    );

    // Only limit to 50 when there's no search input
    if (!inputValue || inputValue.trim() === "") {
      return allContacts.slice(0, 50);
    }

    // When searching, filter by search term and limit to 50 results
    const searchTerm = inputValue.toLowerCase().trim();
    const filtered = allContacts.filter((contact) =>
      contact.name.toLowerCase().includes(searchTerm)
    );
    return filtered.slice(0, 50);
  }, [
    contactsData,
    prospects,
    favorites,
    inputValue,
    excludeContactIds,
    includeContactIds,
    disableFavorites,
    includeProspects,
  ]);

  const hasFavorites = useMemo(
    () =>
      favorites.length > 0 &&
      sortedContacts.some((t) => favorites.includes(t.contactId)),
    [favorites, sortedContacts]
  );

  const toggleFavorite = (contactId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setFavorites((prev) =>
      prev.includes(contactId)
        ? prev.filter((c) => c !== contactId)
        : [...prev, contactId]
    );
  };

  const isFavorite = (contactId: string) => favorites.includes(contactId);

  return (
    <Autocomplete<QueryContact, false, false, false>
      disabled={disabled || loading}
      sx={{ minWidth: 200 }}
      getOptionLabel={(option) => option.name}
      options={sortedContacts}
      inputValue={inputValue}
      value={selectedValue}
      onChange={(event, newValue, reason, details) => {
        setSelectedValue(newValue);
        onChangeProp?.(event, newValue, reason, details);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderOption={(props, option, state) => {
        const { key, ...otherProps } = props;
        const favorite = isFavorite(option.contactId);
        const showSeparator =
          (option as ContactWithSeparator)._showSeparator === true;
        const isProspect =
          (option as ContactWithSeparator)._isProspect === true;
        return (
          <Fragment key={option.contactId}>
            {showSeparator && hasFavorites && (
              <li style={{ pointerEvents: "none" }}>
                <div className="border-t border-border mx-2 my-2" />
              </li>
            )}
            <li {...otherProps}>
              <div className="flex items-center justify-between w-full gap-2">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <span className="truncate">{option.name}</span>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {includeProspects && (
                    <span
                      className={
                        isProspect
                          ? "text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
                          : "text-[10px] font-medium px-1.5 py-0.5 rounded-sm bg-muted text-muted-foreground"
                      }
                    >
                      {isProspect ? "Prospect" : "Contact"}
                    </span>
                  )}
                  <button
                    onClick={(e) => toggleFavorite(option.contactId, e)}
                    className="p-1 hover:bg-muted rounded transition-colors"
                    title={
                      favorite ? "Remove from favorites" : "Add to favorites"
                    }
                  >
                    {!disableFavorites &&
                      (favorite ? (
                        <Star className="size-4 fill-yellow-400 text-yellow-400" />
                      ) : (
                        <Star className="size-4 text-muted-foreground" />
                      ))}
                  </button>
                </div>
              </div>
            </li>
          </Fragment>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size={size}
          placeholder={placeholder}
        />
      )}
      {...props}
    />
  );
};
